import { useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { EmptyState, LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StockActionDialog, type StockTarget } from '@/components/StockActionDialog';
import { useAuth } from '@/auth/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime } from '@/i18n/format';
import type { CountFlag } from '@/api/types';
import { ActionError, describeActionError } from './shared';

/** Positions the system refuses to allocate until someone counts them (a short pick, or a manual mark). */
export function CountFlagsTab(): JSX.Element {
  const { t, locale } = useLanguage();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('INVENTORY.WRITE');
  const result = useApi(() => endpoints.stockCountFlags(), []);
  const [target, setTarget] = useState<StockTarget | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function countNow(f: CountFlag): Promise<void> {
    if (busyId) return;
    setBusyId(f.id);
    setError(null);
    try {
      const res = await endpoints.stockPositions({ locationId: f.location_id, skuId: f.sku_id, ownerId: f.owner_id });
      const here = res.positions.find((p) => p.lot_id === null) ?? null;
      setTarget({
        warehouseId: here?.warehouse_id ?? null,
        locationId: f.location_id,
        locationCode: f.location_code,
        ownerId: f.owner_id,
        ownerName: f.owner_name,
        skuId: f.sku_id,
        skuLabel: `${f.sku_code} — ${f.sku_name}`,
        lotId: null,
        systemQty: here ? here.qty_on_hand : '0',
      });
    } catch (err) {
      setError(describeActionError(t, err));
    } finally {
      setBusyId(null);
    }
  }

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;
  const flags = result.data.flags;

  return (
    <div>
      {error && <ActionError message={error} />}
      {target && (
        <StockActionDialog
          key={`${target.locationId}:${target.skuId}:${target.ownerId}`}
          mode="COUNT"
          target={target}
          onClose={() => setTarget(null)}
          onDone={() => {
            setTarget(null);
            result.reload();
          }}
        />
      )}
      {flags.length === 0 ? (
        <EmptyState title={t('exc.flagsEmpty')} hint={t('exc.flagsHint')} />
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('exc.colLocation')}</th>
                <th>{t('exc.colProduct')}</th>
                <th>{t('exc.colSeller')}</th>
                <th>{t('exc.colWhy')}</th>
                <th>{t('exc.colSince')}</th>
                <th>{t('exc.colOrder')}</th>
                {canWrite && <th>{t('exc.colActions')}</th>}
              </tr>
            </thead>
            <tbody>
              {flags.map((f) => (
                <tr key={f.id}>
                  <td>{f.location_code}</td>
                  <td>
                    {f.sku_code} — {f.sku_name}
                  </td>
                  <td>{f.owner_name}</td>
                  <td>{f.reason === 'SHORT_PICK' ? t('exc.flagReason.SHORT_PICK') : t('exc.flagReason.MANUAL')}</td>
                  <td>{formatDateTime(f.opened_at, locale)}</td>
                  <td>{f.order_number ?? '—'}</td>
                  {canWrite && (
                    <td>
                      <button type="button" className="primary" onClick={() => void countNow(f)} disabled={busyId !== null}>
                        {busyId === f.id ? t('common.loading') : t('exc.countNow')}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
